// src/pages/stock/Suppliers.tsx
//
// Suppliers
// - Uses standard table UI (StandardTableToolbar / StandardTableFrame / StandardPagination)
// - URL is the single source of truth for applied filters and page state
// - Splits search state into:
//   - draftSearch: bound to inputs (editable)
//   - appliedSearch: normalized filters used for API + URL
//
// Navigation
// - Index (#) click -> /stocks/suppliers/:supplierId/edit
// - Supplier Code click -> /stocks/suppliers/:supplierId/edit
// - Add -> /stocks/suppliers/new
//
// Page header
// - Title/description are defined in route handles
// - This page injects header actions via slots (Add Supplier)
// - Clears header slots on unmount to avoid leaking actions

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, TableCell, TableRow } from '@mui/material';
import { Add } from '@mui/icons-material';

import { usePageHeader } from '@components/common/pageHeader.context';

import type { GetSuppliersParams, SupplierResponse } from '@apis/supplier';
import { useSupplierList } from '@queries/suppliers/useSupplierList';
import { useSupplierCount } from '@queries/suppliers/useSupplierCount';

import SuppliersSearchPanel from '@components/stock/SuppliersSearchPanel';
import type { SetSearchValueFn } from '@components/common/search';

import { filterSearchParams } from '@utils/search';
import { timestampToDate } from '@utils/time';

import {
  StandardPagination,
  StandardTableFrame,
  StandardTableToolbar,
  cellSx,
} from '@components/common/table';

const PAGE_SIZE = 50;
const PAGE_AUTO_SEARCH = { enabled: false, delayMs: 500 } as const;

type SearchForm = GetSuppliersParams;

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

const normalizeText = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

const normalizeSearch = (form: SearchForm): SearchForm => {
  const supplierName = normalizeText(form.supplierName);
  const managerName = normalizeText(form.managerName);
  const email = normalizeText(form.email);
  const phoneNumber = normalizeText(form.phoneNumber);

  return {
    supplierName: supplierName || undefined,
    managerName: managerName || undefined,
    email: email || undefined,
    phoneNumber: phoneNumber || undefined,
  };
};

const isSameSearch = (a: SearchForm, b: SearchForm) => {
  const x = normalizeSearch(a);
  const y = normalizeSearch(b);

  return (
    (x.supplierName ?? '') === (y.supplierName ?? '') &&
    (x.managerName ?? '') === (y.managerName ?? '') &&
    (x.email ?? '') === (y.email ?? '') &&
    (x.phoneNumber ?? '') === (y.phoneNumber ?? '')
  );
};

const readSearchFromUrl = (sp: URLSearchParams): SearchForm => ({
  supplierName: sp.get('supplierName') || '',
  managerName: sp.get('managerName') || '',
  email: sp.get('email') || '',
  phoneNumber: sp.get('phoneNumber') || '',
});

const buildUrlParams = (form: SearchForm, page: number) => {
  const cleaned = normalizeSearch(form);

  const params: Record<string, string> = {
    ...(cleaned.supplierName ? { supplierName: String(cleaned.supplierName) } : {}),
    ...(cleaned.managerName ? { managerName: String(cleaned.managerName) } : {}),
    ...(cleaned.email ? { email: String(cleaned.email) } : {}),
    ...(cleaned.phoneNumber ? { phoneNumber: String(cleaned.phoneNumber) } : {}),
    page: String(page),
  };

  return filterSearchParams(params);
};

const Suppliers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setPageHeaderSlots } = usePageHeader();

  /**
   * appliedSearch:
   *   Represents the active filters synced with URL and API calls.
   * draftSearch:
   *   Represents the editable state inside the search panel UI.
   */
  const [appliedSearch, setAppliedSearch] = useState<SearchForm>(() =>
    readSearchFromUrl(searchParams),
  );
  const [draftSearch, setDraftSearch] = useState<SearchForm>(() => ({
    ...readSearchFromUrl(searchParams),
  }));

  const [page, setPage] = useState<number>(() => {
    const n = Number(searchParams.get('page'));
    return Number.isFinite(n) && n > 0 ? n : 1;
  });

  const [filtersOpen, setFiltersOpen] = useState(false);

  /**
   * Registers page-level header actions.
   * Actions are explicitly cleared on unmount to avoid leakage.
   */
  useEffect(() => {
    setPageHeaderSlots({
      actions: [
        {
          key: 'add-supplier',
          label: 'Add Supplier',
          startIcon: <Add />,
          onClick: () => navigate('/stocks/suppliers/new'),
          variant: 'contained',
          primary: true,
        },
      ],
    });

    return () => setPageHeaderSlots({});
  }, [navigate, setPageHeaderSlots]);

  /**
   * Synchronizes component state from URL parameters.
   */
  useEffect(() => {
    const nextApplied = readSearchFromUrl(searchParams);

    const rawPage = Number(searchParams.get('page'));
    const nextPage = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;

    setAppliedSearch(nextApplied);
    setDraftSearch((prev) => (isSameSearch(prev, nextApplied) ? prev : nextApplied));
    setPage(nextPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const cleanedApplied = useMemo(
    () => normalizeSearch(appliedSearch),
    [appliedSearch],
  );

  // Queries
  const { data, isLoading: isLoadingList } = useSupplierList({
    page,
    searchParam: cleanedApplied,
  });
  const { data: totalCount, isLoading: isLoadingCount } =
    useSupplierCount(cleanedApplied);

  const safeTotalCount = totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(safeTotalCount / PAGE_SIZE));
  const rows: SupplierResponse[] = useMemo(() => data?.data ?? [], [data]);

  const isTableLoading = Boolean(isLoadingList || isLoadingCount);

  /**
   * Setter for shared SearchPanel components.
   */
  const setValue: SetSearchValueFn<SearchForm> = useCallback((key, value) => {
    setDraftSearch((prev) => {
      const v = typeof value === 'string' ? value : '';

      switch (key) {
        case 'supplierName':
        case 'managerName':
        case 'email':
        case 'phoneNumber':
          return { ...prev, [key]: v };
        default:
          return prev;
      }
    });
  }, []);

  // Apply / Reset
  const applyFilters = useCallback(
    (nextDraft?: SearchForm) => {
      const next = nextDraft ?? draftSearch;

      setAppliedSearch(next);
      setPage(1);
      setSearchParams(buildUrlParams(next, 1));
    },
    [draftSearch, setSearchParams],
  );

  const handleSearch = useCallback(() => applyFilters(), [applyFilters]);

  const handleReset = useCallback(() => {
    const cleared: SearchForm = {
      supplierName: '',
      managerName: '',
      email: '',
      phoneNumber: '',
    };

    setDraftSearch(cleared);
    applyFilters(cleared);
  }, [applyFilters]);

  const handleChangePage = (next: number) => {
    const safeNext = clamp(next, 1, totalPages);
    setPage(safeNext);
    setSearchParams(buildUrlParams(appliedSearch, safeNext));
  };

  const handleOpenEditPage = useCallback(
    (supplierId: number) => navigate(`/stocks/suppliers/${supplierId}/edit`),
    [navigate],
  );

  const columnsCount = 7;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box mt={2}>
        <SuppliersSearchPanel
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          draftSearch={draftSearch}
          setValue={setValue}
          onSearch={handleSearch}
          onReset={handleReset}
          autoSearch={PAGE_AUTO_SEARCH}
        />
      </Box>

      <StandardTableToolbar
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={safeTotalCount}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      <Box mt={1}>
        <StandardTableFrame<SupplierResponse>
          containerSx={{ borderRadius: 2 }}
          minWidth={980}
          columnsCount={columnsCount}
          isLoading={isTableLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText="No suppliers found."
          head={
            <TableRow>
              <TableCell width={80} align="right">
                #
              </TableCell>
              <TableCell sx={{ minWidth: 160 }}>Supplier Code</TableCell>
              <TableCell sx={{ minWidth: 200 }}>Supplier</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Manager</TableCell>
              <TableCell sx={{ minWidth: 220 }}>E-mail</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Phone number</TableCell>
              <TableCell width={220}>Created</TableCell>
            </TableRow>
          }
          rows={rows}
          getRowKey={(row) => String(row.supplierId)}
          renderRow={(row, index) => {
            const displayIndex = (page - 1) * PAGE_SIZE + index + 1;

            return (
              <>
                <TableCell align="right" sx={cellSx.number}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => handleOpenEditPage(row.supplierId)}
                    sx={{
                      minWidth: 0,
                      px: 0.5,
                      fontWeight: 800,
                      textDecoration: 'underline',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    {displayIndex}
                  </Button>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => handleOpenEditPage(row.supplierId)}
                    sx={{
                      px: 0,
                      minWidth: 0,
                      textTransform: 'none',
                      fontWeight: 800,
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    }}
                  >
                    {row.supplierCode || '-'}
                  </Button>
                </TableCell>

                <TableCell sx={{ minWidth: 200, wordBreak: 'break-word' }}>
                  {row.supplierName || '-'}
                </TableCell>
                <TableCell sx={cellSx.nowrap}>{row.managerName || '-'}</TableCell>
                <TableCell sx={cellSx.nowrap}>{row.email || '-'}</TableCell>
                <TableCell sx={cellSx.nowrap}>{row.phoneNumber || '-'}</TableCell>
                <TableCell sx={cellSx.nowrap}>
                  {timestampToDate(row.createdAt) || '-'}
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={safeTotalCount}
          onChangePage={handleChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default Suppliers;
